<i18n>
{
	"en": {
		"TodoName": "Todo name"
	},
	"vi": {
		"TodoName": "Tên công việc"
	}
}
</i18n>

<template>
	<div class="cbs-todo-check-list">
		<draggable name="cbs-todo-check-list-config" :delay="200" :disabled="isReadonly" :delayOnTouchOnly="true" v-model="localValue" :handle="'.item__btn-drag'" @end="$emit('input', localValue)">
			<transition-group tag="ul" name="slide-fade" class="todo-items cbs-todo-check-list__todo-items">
				<li
					:class="[
						'item todo-items__item mb-3 border-solid border border-gray-5 flex items-center justify-between overflow-hidden',
						{
							'cursor-pointer': !isReadonly,
							'todo-items__item--canceled': isTodoItemCanceled(todoItem.properties)
						}
					]"
					v-for="todoItem in localValue"
					:key="todoItem.id"
					@click="handleClickTodoItem($event, todoItem)"
				>
					<div class="w-3/5 overflow-hidden relative">
						<vx-tooltip
							:class="['todo-item-color-status w-1 absolute left-0 top-1/2', renderBackgroundStatusColor(todoItem.status_object.key)]"
							:text="todoItem.status_object.value"
							position="top"
						>
						</vx-tooltip>
						<i v-if="isSortable" class="item__btn-drag nucleo nucleo-ic_drag_handle_24px text-gray-3 text-xl cursor-move" />
						<slot name="todo-item-editable" :todoItem="todoItem" v-if="!isReadonly">
							<cbs-input
								v-validate="'required'"
								:class="[
									'w-full item__input',
									{
										'item__input--error': errors.first(`todo-item-${todoItem.id}`)
									}
								]"
								v-model="todoItem.name"
								:title="todoItem.name"
								:data-vv-as="$t('TodoName')"
								:name="`todo-item-${todoItem.id}`"
								:placeholder="errors.first(`todo-item-${todoItem.id}`)"
								@blur="handleChangeTodoItemNameEditMode(todoItem)"
								@keyup.enter="handleChangeTodoItemNameEditMode(todoItem)"
							/>
						</slot>
						<slot name="todo-item-readonly" :todoItem="todoItem" v-else-if="isReadonly">
							<cbs-input readonly class="w-full item__input item__input--readonly-mode" :value="todoItem.name" />
						</slot>
					</div>
					<div class="w-2/5 flex justify-between items-center pr-3">
						<cbs-list-avatars :maxItem="2" :value="todoItem.employees" textKey="name" avatarKey="avatar" color="#F2F2F2" />
						<span v-if="todoItem.properties" :class="['text-base', { 'text-over-time': todoItem.is_over_due }]">{{ todoItem.properties.deadline | dateTime(userDateFormat) }}</span>
						<slot name="context-menu" :todoItem="todoItem"></slot>
					</div>
				</li>
			</transition-group>
		</draggable>

		<div class="cbs-todo-check-list__input" v-if="!isReadonly">
			<cbs-input class="w-full" v-model="newTodoItem" :placeholder="inputPlaceholder" @keyup.enter="handleAddNewOption" />
		</div>
	</div>
</template>

<script>
import { cloneDeep as lodashCloneDeep } from "lodash";

import Draggable from "vuedraggable";
import CbsListAvatars from "@/components/cbs-list-avatars/CbsListAvatars";

const isValidStatusObject = status => status.hasOwnProperty("key") && status.hasOwnProperty("value");
const isValidEmployeeObject = employee => employee.hasOwnProperty("name") && employee.hasOwnProperty("avatar");
const isValidValue = value =>
	value.hasOwnProperty("id") &&
	value.hasOwnProperty("name") &&
	value.hasOwnProperty("properties") &&
	value.hasOwnProperty("can_cancel") &&
	value.hasOwnProperty("can_update") &&
	value.hasOwnProperty("employees") &&
	Array.isArray(value["employees"]) &&
	value["employees"].every(employee => !!isValidEmployeeObject(employee)) &&
	value.hasOwnProperty("changable_statuses") &&
	Array.isArray(value["changable_statuses"]) &&
	value["changable_statuses"].every(status => !!isValidStatusObject(status));

export default {
	inheritAttrs: false,
	name: "CbsTodoCheckList",
	components: { Draggable, CbsListAvatars },
	props: {
		value: {
			type: Array,
			required: true,
			validator: propValue => propValue.length === 0 || propValue.every(objPropValue => isValidValue(objPropValue))
		},
		isReadonly: {
			type: Boolean,
			default: false
		},
		isSortable: {
			type: Boolean,
			default: false
		},
		inputPlaceholder: String
	},
	emits: {
		onEditTodo: {},
		onEnterNewTodo: {}
	},
	data() {
		return {
			newTodoItem: "",

			currentId: 1,
			currentEditOption: {
				id: null,
				name: ""
			},

			localValue: []
		};
	},
	watch: {
		value() {
			this.setInitLocalValue();
		}
	},
	methods: {
		async handleAddNewOption() {
			if (this.newTodoItem.trim()) {
				await new Promise((resolve, reject) => this.$emit("onEnterNewTodo", this.newTodoItem, resolve, reject));
				this.newTodoItem = "";
			}
		},
		handleClickTodoItem(event, todoItem) {
			const eventClasses = event.target && event.target.className;
			// show detail dialog if click target is not below classes
			if (event.target !== event.currentTarget && !eventClasses.split(" ").find(className => ["cbs-context-menu"].includes(className))) {
				if (this.isReadonly) {
					this.$emit("onClickTodoItem", todoItem);
				} else {
					this.handleToggleEditMode(todoItem);
				}
			}
		},
		handleToggleEditMode(todoItem) {
			this.currentEditOption = {
				id: todoItem.id,
				name: todoItem.name
			};
		},
		async handleChangeTodoItemNameEditMode(todoItemEditing) {
			const initTodoItemEditing = this.value.find(tempTodoItem => tempTodoItem.id === todoItemEditing.id) || {};
			if (!todoItemEditing.name || !todoItemEditing.name.trim()) {
				this.resetLocalTodoItemName(todoItemEditing, initTodoItemEditing);
				this.$validator.reset();
			} else if (todoItemEditing.name !== initTodoItemEditing.name) {
				try {
					await new Promise((resolve, reject) => this.$emit("onEditTodo", todoItemEditing, resolve, reject));
				} catch (error) {
					this.resetLocalTodoItemName(todoItemEditing, initTodoItemEditing);
				}
			}
		},
		setInitLocalValue() {
			this.localValue = lodashCloneDeep(this.value).map(objValue => ({
				...objValue,
				properties: typeof objValue.properties === "string" ? JSON.parse(objValue.properties) : {}
			}));
		},
		resetLocalTodoItemName(todoItem, initTodoItemEditing) {
			this.localValue = this.localValue.map(tempTodoItem => {
				if (tempTodoItem.id === todoItem.id) {
					return {
						...todoItem,
						name: initTodoItemEditing.name
					};
				}
				return tempTodoItem;
			});
		},
		isTodoItemCanceled(todoItemProperties) {
			if (typeof todoItemProperties === "string") {
				const parsedProperties = JSON.parse(todoItemProperties) || {};
				return parsedProperties.status && parsedProperties.status === this.$constant.TODO_ITEM_STATUSES.STATUS_CANCELED;
			}
			return false;
		}
	},
	created() {
		this.setInitLocalValue();
	}
};
</script>

<style lang="scss" scoped>
.cbs-todo-check-list {
	.todo-items__item,
	::v-deep .cbs-todo-check-list__input input {
		border-radius: 4px !important;
		border: 1px solid var(--color-gray-6) !important;
	}

	.todo-items__item {
		height: 56px;

		::v-deep .item__input input,
		::v-deep .vs-input--placeholder {
			border: 0 !important;
			box-shadow: none !important;
		}

		&.todo-items__item--canceled,
		&.todo-items__item--canceled .item__input ::v-deep input {
			background-color: var(--color-gray-10);
		}

		.item__input--error {
			::v-deep input,
			::v-deep .vs-input--placeholder {
				color: var(--color-solid-red) !important;
			}
		}

		::v-deep .item__input--readonly-mode input {
			cursor: pointer;
		}
	}
	.todo-item-color-status {
		height: 65%;
		top: 50%;
		transform: translateY(-50%);
	}
}
</style>
